import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getEmployeeDashboard } from '../api/dashboard';
import { updateTicket } from '../api/tickets';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import {
  Ticket,
  CheckCircle,
  Clock,
  AlertCircle,
  UserPlus,
  User,
  Building,
  ArrowRight,
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Button from '../components/ui/button';

const DASHBOARD_PAGE_SIZE = 5;

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigningTicket, setAssigningTicket] = useState(null);
  const [assignedPage, setAssignedPage] = useState(1);
  const [unassignedPage, setUnassignedPage] = useState(1);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await getEmployeeDashboard();
      if (response.success) {
        setDashboardData(response.data);
        setAssignedPage(1);
        setUnassignedPage(1);
      }
    } catch (error) {
      console.error('Failed to fetch employee dashboard data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.response?.status === 403) {
        toast.error('Access Denied', {
          description: 'You do not have permission to access this dashboard'
        });
      } else if (error.response?.status === 401) {
        toast.error('Authentication Failed', {
          description: 'Please log in again'
        });
        navigate('/login');
      } else {
        toast.error('Failed to load dashboard data', {
          description: error.response?.data?.message || error.message
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToSelf = async (ticketId) => {
    try {
      setAssigningTicket(ticketId);
      await updateTicket(ticketId, {
        assigned_to: user.id,
        status: 'in_progress'
      });
      toast.success('Ticket assigned to you');
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to assign ticket:', error);
      toast.error('Failed to assign ticket');
    } finally {
      setAssigningTicket(null);
    }
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout userRole={user?.role}>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
          <div className="w-full max-w-none p-4 sm:p-6">
            <div className="animate-pulse space-y-6">
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg w-64"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const stats = dashboardData || {};
  const assignedTickets = stats.recentTickets || [];
  const unassignedTickets = stats.unassignedTickets || [];
  const assignedPages = Math.max(1, Math.ceil(assignedTickets.length / DASHBOARD_PAGE_SIZE));
  const unassignedPages = Math.max(1, Math.ceil(unassignedTickets.length / DASHBOARD_PAGE_SIZE));
  const safeAssignedPage = Math.min(assignedPage, assignedPages);
  const safeUnassignedPage = Math.min(unassignedPage, unassignedPages);
  const paginatedAssignedTickets = assignedTickets.slice(
    (safeAssignedPage - 1) * DASHBOARD_PAGE_SIZE,
    safeAssignedPage * DASHBOARD_PAGE_SIZE
  );
  const paginatedUnassignedTickets = unassignedTickets.slice(
    (safeUnassignedPage - 1) * DASHBOARD_PAGE_SIZE,
    safeUnassignedPage * DASHBOARD_PAGE_SIZE
  );

  const renderPagination = (currentPage, totalPages, totalItems, onPageChange, accent = 'indigo') => {
    if (totalItems <= DASHBOARD_PAGE_SIZE) return null;

    const startItem = (currentPage - 1) * DASHBOARD_PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * DASHBOARD_PAGE_SIZE, totalItems);
    const accentStyles = {
      indigo: 'bg-indigo-600 hover:bg-indigo-700 border-indigo-600 text-white',
      purple: 'bg-purple-600 hover:bg-purple-700 border-purple-600 text-white'
    };
    const activeClass = accentStyles[accent] || accentStyles.indigo;

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Showing {startItem}-{endItem} of {totalItems}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="h-8 px-2.5"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                size="sm"
                variant={page === currentPage ? 'default' : 'outline'}
                onClick={() => onPageChange(page)}
                className={`h-8 min-w-8 px-2 ${page === currentPage ? activeClass : ''}`}
              >
                {page}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="h-8 px-2.5"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="w-full max-w-none p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  Welcome back, {(user?.name?.replace(/0+$/, '').trim() || 'Employee').split(' ')[0]}!
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                  Manage your assigned tickets and claim new ones
                </p>
              </div>
              <Button
                onClick={fetchDashboardData}
                variant="outline"
                className="flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-all duration-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-100 dark:bg-indigo-900/20 rounded-bl-full opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    My Assigned
                  </span>
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <Ticket className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">
                  {stats.assignedTotal || 0}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Tickets assigned to you
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-all duration-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-100 dark:bg-yellow-900/20 rounded-bl-full opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    In Progress
                  </span>
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">
                  {stats.inProgress || 0}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Currently working on
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-all duration-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-bl-full opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    Resolved
                  </span>
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">
                  {stats.resolvedThisMonth || 0}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Completed this month
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-all duration-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-purple-100 dark:bg-purple-900/20 rounded-bl-full opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    Available
                  </span>
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <UserPlus className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">
                  {stats.unassigned || 0}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Unassigned tickets
                </p>
              </div>
            </div>
          </div>

          {/* Overdue Warning */}
          {stats.overdue > 0 && (
            <div className="mb-6 sm:mb-8 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-l-4 border-red-500 dark:border-red-400 rounded-lg p-4 flex items-center gap-3 shadow-sm">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900 dark:text-red-300">
                  Action Required
                </p>
                <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                  You have {stats.overdue} overdue ticket{stats.overdue > 1 ? 's' : ''} that need immediate attention
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/admin/tickets?filter=my&status=open')}
                className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                View Tickets
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* My Assigned Tickets */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/10 dark:to-blue-900/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <Ticket className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                        My Assigned Tickets
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {stats.assignedTotal || 0} total assigned
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin/tickets?filter=my')}
                    className="text-sm hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
                  >
                    <span className="hidden sm:inline">View All</span>
                    <ArrowRight className="w-4 h-4 sm:ml-1" />
                  </Button>
                </div>
              </div>
              <div className="p-4 sm:p-6 max-h-[600px] overflow-y-auto">
                {assignedTickets.length > 0 ? (
                  <div className="space-y-3">
                    {paginatedAssignedTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-md transition-all duration-200 cursor-pointer group bg-white dark:bg-slate-800"
                        onClick={() => handleViewTicket(ticket.id)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                                {ticket.ticket_number}
                              </h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                                {ticket.status?.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 font-medium">
                              {ticket.subject}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-3">
                          <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority?.toUpperCase()}
                          </span>
                          {ticket.customer_name && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                              <User className="w-3 h-3" />
                              {ticket.customer_name}
                            </span>
                          )}
                          {ticket.category_name && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                              {ticket.category_name}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Created {formatDate(ticket.created_at)}
                          </span>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
                        </div>
                      </div>
                    ))}
                    {renderPagination(
                      safeAssignedPage,
                      assignedPages,
                      assignedTickets.length,
                      setAssignedPage,
                      'indigo'
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                      <Ticket className="w-8 h-8 opacity-50" />
                    </div>
                    <p className="font-medium mb-1">No assigned tickets</p>
                    <p className="text-xs">You don't have any tickets assigned to you yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Unassigned Tickets */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <UserPlus className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                        Available to Claim
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {stats.unassigned || 0} unassigned tickets
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin/tickets/unassigned')}
                    className="text-sm hover:bg-purple-100 dark:hover:bg-purple-900/30"
                  >
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
              <div className="p-4 sm:p-6 max-h-[600px] overflow-y-auto">
                {unassignedTickets.length > 0 ? (
                  <div className="space-y-3">
                    {paginatedUnassignedTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all duration-200 bg-white dark:bg-slate-800"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                                {ticket.ticket_number}
                              </h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                                {ticket.status?.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 font-medium">
                              {ticket.subject}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-3">
                          <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority?.toUpperCase()}
                          </span>
                          {ticket.customer_name && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                              <User className="w-3 h-3" />
                              {ticket.customer_name}
                            </span>
                          )}
                          {ticket.customer_company && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                              <Building className="w-3 h-3" />
                              {ticket.customer_company}
                            </span>
                          )}
                          {ticket.category_name && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                              {ticket.category_name}
                            </span>
                          )}
                        </div>
                        <div className="mt-4 flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAssignToSelf(ticket.id)}
                            disabled={assigningTicket === ticket.id}
                            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
                          >
                            {assigningTicket === ticket.id ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Assigning...
                              </>
                            ) : (
                              <>
                                <UserPlus className="w-4 h-4 mr-2" />
                                Claim Ticket
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewTicket(ticket.id)}
                            className="border-gray-300 dark:border-gray-600"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Created {formatDate(ticket.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                    {renderPagination(
                      safeUnassignedPage,
                      unassignedPages,
                      unassignedTickets.length,
                      setUnassignedPage,
                      'purple'
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                      <UserPlus className="w-8 h-8 opacity-50" />
                    </div>
                    <p className="font-medium mb-1">No unassigned tickets</p>
                    <p className="text-xs">All tickets are currently assigned</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeDashboard;


